import {
  type BabyQuote,
  type CamperQuote,
  type CarQuote,
  type CatQuote,
  type ChildQuote,
  type Coverage,
  type DogQuote,
  type HouseholderQuote,
  type Quote,
  type QuoteCoverage,
  TermPeriod,
  type TravelQuote,
  type VanQuote,
} from "@/api_client";
import { matchProductType } from "@/utils/productUtils";
import type { InsuranceInfo } from "../types/types";
import { getCoverage } from "./getCoverage";
import { getProductPrice } from "./price";

const insuranceInfoUnknown = (
  contextId: string,
): Omit<
  InsuranceInfo,
  | "eventTime"
  | "startdate"
  | "enddate"
  | "youngDriver"
  | "mileage"
  | "duration"
  | "insuranceDeductable"
  | "duedate"
  | "insuranceTravel"
  | "coveredAmount"
  | "bonus"
> => {
  return {
    coverage: undefined,
    price: undefined,
    quoteId: contextId,
    insuranceAddons: undefined,
  };
};

const getInsuranceInfo = (
  contextId: string,
  product: Quote,
  coveragesByCoreName: Record<string, Coverage> | undefined,
  timestamp?: Date,
): InsuranceInfo => {
  const addons =
    product.coverages
      ?.filter((coverage) => coverage.isAddon && coverage.selected)
      ?.map((addon) => ({
        value: getAddonShortName(addon),
      })) || [];

  return addProductSpecificData(
    {
      price: getProductPrice(product),
      coverage: getCoverage(product, coveragesByCoreName),
      quoteId: contextId,
      eventTime: timestamp && Math.floor(timestamp.getTime() / 1000), // Unix seconds
      startdate: undefined,
      enddate: undefined,
      youngDriver: undefined,
      mileage: undefined,
      duration: undefined,
      insuranceAddons: addons,
      insuranceDeductable: [],
      duedate: undefined,
      insuranceTravel: {
        type: undefined,
        destination: undefined,
      },
      coveredAmount: undefined,
      bonus: [
        {
          type: "Easystart",
          value: "No", // Not applicable right now for other products than car insurance
        },
      ],
    },
    product,
  );
};

const getAddonShortName = (coverage: QuoteCoverage) => {
  switch (coverage?.coverageName) {
    case "TravelBaggageSecurityFixedTerm":
    case "TravelBaggageSecurityContinuousTerm":
      return "MT";
    case "TravelPassengerSecurityContinuousTermDomesticAndForeignCountries":
      return "KM";
    case "HouseholderApartmentLarge":
    case "HouseholderSemiDetachedLarge":
      return "LP";
    case "BabyCriticalIllness":
      return "PS";
    case "ChildCriticalIllness":
      return "PS";
    case "DogLiabilityInsurance":
      return "VT";
    case "DogPhysicalTreatment":
      return "KT";
    case "CarWindscreen":
    case "VanWindscreen":
    case "CamperWindscreen":
      return "LT";
    case "CarRedemptionProtection":
    case "VanRedemption":
    case "CamperRedemption":
      return "SA";
    case "CarReplacement":
    case "VanReplacement":
    case "CamperReplacement":
      return "PL";
  }
  throw new Error(
    `Couldn't map to short name: ${coverage?.coverageName ?? ""}`,
  );
};

const mapTravelInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  travel: TravelQuote,
): InsuranceInfo => {
  // Map the term period to short code; return undefined if not present.
  const mapTermPeriod = (
    termPeriod: TermPeriod | undefined | null,
  ): string | undefined => {
    if (!termPeriod) return undefined;
    if (termPeriod === TermPeriod.FIXED) return "FIX";
    if (termPeriod === TermPeriod.CONTINUOUS) return "CON";
    return termPeriod;
  };

  return {
    ...insuranceInfo,
    startdate: travel.coveragePeriod?.startDate ?? undefined,
    enddate: travel.coveragePeriod?.endDate ?? undefined,
    insuranceTravel: {
      destination: travel.destinationCountry ?? undefined,
      type: mapTermPeriod(travel.coveragePeriod?.termPeriod),
    },
  };
};

const mapHouseholderInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  householder: HouseholderQuote,
): InsuranceInfo => {
  const selectedCoverage = householder.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    startdate: householder.terms?.startDate ?? undefined,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const mapBabyInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  baby: BabyQuote,
): InsuranceInfo => {
  const selectedCoverage = baby.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    duedate: baby.expectedDateOfBirth ?? undefined,
    coveredAmount: `Vauvavakuutus:${selectedCoverage?.insuranceAmount}`,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const mapChildInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  child: ChildQuote,
): InsuranceInfo => {
  const selectedCoverage = child.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    coveredAmount: `wide:${selectedCoverage?.insuranceAmount}`,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const mapPetInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  quote: Quote,
): InsuranceInfo => {
  const dogQuote = quote as DogQuote;
  const catQuote = quote as CatQuote;
  const selectedCoverage = quote.coverages?.find(
    (c) => c.selected && c.hasInsuranceAmount,
  );
  return {
    ...insuranceInfo,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.insuranceAmount ?? 0 },
    ],
    startdate:
      dogQuote?.dogCovered?.startDate ??
      catQuote?.catCovered?.startDate ??
      undefined,
  };
};

const mapCarInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  product: Quote,
): InsuranceInfo => {
  const carQuote = product as CarQuote;
  const selectedCoverage = carQuote.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    bonus: [
      {
        type: "Easystart",
        value: "No", // HOW WE KNOW?
      },
    ],
    youngDriver: carQuote?.driverIsYoung ?? undefined,
    mileage: carQuote?.annualMileage ?? undefined,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const mapVanInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  product: Quote,
): InsuranceInfo => {
  const vanQuote = product as VanQuote;
  const selectedCoverage = vanQuote.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const mapCamperInsuranceInfo = (
  insuranceInfo: InsuranceInfo,
  product: Quote,
): InsuranceInfo => {
  const camperQuote = product as CamperQuote;
  const selectedCoverage = camperQuote.coverages?.find((c) => c.selected);
  return {
    ...insuranceInfo,
    insuranceDeductable: [
      { type: "product", value: selectedCoverage?.deductibleAmount ?? 0 },
    ],
  };
};

const addProductSpecificData = (
  insuranceInfo: InsuranceInfo,
  product: Quote,
): InsuranceInfo => {
  return matchProductType(product.productType, {
    travel: mapTravelInsuranceInfo(insuranceInfo, product as TravelQuote),
    householder: mapHouseholderInsuranceInfo(
      insuranceInfo,
      product as HouseholderQuote,
    ),
    baby: mapBabyInsuranceInfo(insuranceInfo, product as BabyQuote),
    car: mapCarInsuranceInfo(insuranceInfo, product),
    income: insuranceInfo,
    sport: insuranceInfo,
    child: mapChildInsuranceInfo(insuranceInfo, product as ChildQuote),
    cat: mapPetInsuranceInfo(insuranceInfo, product),
    dog: mapPetInsuranceInfo(insuranceInfo, product),
    van: mapVanInsuranceInfo(insuranceInfo, product),
    camper: mapCamperInsuranceInfo(insuranceInfo, product),
    "": insuranceInfo,
  });
};

export default {
  insuranceInfoUnknown,
  getInsuranceInfo,
};
